"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  NumberInput,
  SelectInput,
  CheckboxRow,
  ResultBanner,
  PrintButton,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* CDM 2015 (Construction (Design and Management) Regulations 2015).

   F10 notification — regulation 6: a project is notifiable to HSE if the
   construction work is scheduled to:
     - last longer than 30 working days AND have more than 20 workers working
       simultaneously at any point in the project, OR
     - exceed 500 person-days.
   Verified against the rams-knowledge bank (quotable HSE source,
   hse.gov.uk/construction/cdm/2015/index.htm) on 2026-06-06.

   This tool gives guidance on dutyholder roles and the F10 verdict. It is not
   legal advice — duties depend on the specifics of each project and must be
   confirmed against HSE guidance and the Approved Code of Practice L153. */

type ClientType = "" | "domestic" | "commercial";
type Role =
  | ""
  | "client"
  | "designer"
  | "principal-designer"
  | "contractor"
  | "principal-contractor"
  | "sole-trader";

const ROLE_LABELS: Record<Exclude<Role, "">, string> = {
  client: "Client",
  designer: "Designer",
  "principal-designer": "Principal designer",
  contractor: "Contractor",
  "principal-contractor": "Principal contractor",
  "sole-trader": "Sole trader doing the work",
};

interface Duty {
  text: string;
}

interface Outcome {
  notifiable: boolean;
  level: Verdict;
  verdictTitle: string;
  verdictBody: string;
  duties: Duty[];
  needsCpp: boolean;
}

function buildOutcome(
  client: ClientType,
  role: Role,
  multipleContractors: boolean,
  bigOver30: boolean,
  over500: boolean,
): Outcome {
  // F10 / reg 6 test.
  const notifiable = bigOver30 || over500;

  const verdictTitle = notifiable
    ? "Likely F10 NOTIFIABLE to HSE"
    : "Likely NOT F10 notifiable";

  const verdictBody = notifiable
    ? "On the durations you entered this project meets the CDM 2015 reg 6 threshold (longer than 30 working days with more than 20 workers on site at once, or over 500 person-days), so it must be notified to HSE on form F10 before construction work starts. CDM still applies whether or not a project is notifiable — notification is an extra duty, not the trigger for the regulations."
    : "On the durations you entered this project does not meet the CDM 2015 reg 6 notification threshold, so no F10 is required. Important: every CDM duty below still applies — CDM 2015 applies to all construction work regardless of size or duration. Re-check if the programme grows.";

  const level: Verdict = notifiable ? "high" : "low";

  // A construction phase plan is required on EVERY project under CDM 2015.
  const needsCpp = true;

  const duties: Duty[] = [];

  // Appointments: PD + PC required when more than one contractor.
  if (multipleContractors) {
    duties.push({
      text:
        "More than one contractor: the client must appoint a principal designer (to plan and coordinate the pre-construction phase) and a principal contractor (to plan and coordinate the construction phase) — in writing.",
    });
  } else {
    duties.push({
      text:
        "Single contractor: no principal designer or principal contractor is required. The sole contractor takes on the duty to plan, manage and monitor the construction phase, including producing the construction phase plan.",
    });
  }

  // Construction phase plan on every project.
  duties.push({
    text:
      "A construction phase plan (CPP) is required on every project, including single-contractor and domestic jobs. It must be in place before construction work starts and be proportionate to the risks of the work.",
  });

  // Domestic client transfer rule.
  if (client === "domestic") {
    duties.push({
      text:
        "Domestic client: the client's CDM duties pass automatically to the contractor (single contractor) or to the principal contractor (more than one contractor). They can instead be carried out by the principal designer if there is a written agreement. The householder is not expected to manage the work themselves.",
    });
    duties.push({
      text:
        "F10 (where notifiable): on a domestic project the duty to notify falls to the contractor or principal contractor, not the householder.",
    });
  } else if (client === "commercial") {
    duties.push({
      text:
        "Commercial/organisation client: the client must make suitable arrangements for managing the project, provide pre-construction information to designers and contractors, ensure welfare is provided, and (where notifiable) submit the F10 notification to HSE before work starts.",
    });
  }

  // Welfare.
  duties.push({
    text:
      "Welfare: suitable welfare facilities (toilets, washing, drinking water, rest/changing) must be provided from the start and maintained throughout — Schedule 2 of CDM 2015.",
  });

  // Role-specific line.
  if (role) {
    duties.push({ text: roleDuty(role, multipleContractors, client) });
  }

  return { notifiable, level, verdictTitle, verdictBody, duties, needsCpp };
}

function roleDuty(role: Role, multipleContractors: boolean, client: ClientType): string {
  switch (role) {
    case "client":
      return "Your role (client): you carry the primary duty to make suitable arrangements, allow adequate time and resources, provide pre-construction information, and make the right appointments in writing. On a commercial project you also submit the F10 if notifiable.";
    case "designer":
      return "Your role (designer): eliminate, reduce or control foreseeable risks through design; provide information about remaining risks; and check the client is aware of their duties before starting design work.";
    case "principal-designer":
      return "Your role (principal designer): plan, manage and monitor the pre-construction phase, coordinate health and safety, compile pre-construction information and prepare the health and safety file.";
    case "contractor":
      return multipleContractors
        ? "Your role (contractor): plan, manage and monitor your own work, comply with directions from the principal contractor, and not start work until the relevant parts of the construction phase plan are in place."
        : "Your role (contractor) as the only contractor: you take on the principal-contractor-style duties — plan, manage and monitor the construction phase and produce the construction phase plan.";
    case "principal-contractor":
      return "Your role (principal contractor): plan, manage, monitor and coordinate the construction phase; draw up and keep the construction phase plan up to date; organise cooperation between contractors; and secure the site and welfare.";
    case "sole-trader":
      return client === "domestic"
        ? "Your role (sole trader on a domestic job): as the only contractor you typically inherit the householder's CDM duties — plan, manage and monitor the work, provide welfare, and produce a proportionate construction phase plan before you start."
        : "Your role (sole trader): you are a contractor under CDM and, as the only contractor, take on the duty to plan, manage and monitor the construction phase and produce the construction phase plan.";
    default:
      return "";
  }
}

export function CdmDutyChecker() {
  const [client, setClient] = useState<ClientType>("");
  const [role, setRole] = useState<Role>("");
  const [multipleContractors, setMultipleContractors] = useState(false);
  const [bigOver30, setBigOver30] = useState(false);
  const [over500, setOver500] = useState(false);
  // Person-days helper inputs (workers × days, live).
  const [pdWorkers, setPdWorkers] = useState("");
  const [pdDays, setPdDays] = useState("");
  const resultTracked = useRef(false);

  const personDays = useMemo(() => {
    const w = parseFloat(pdWorkers);
    const d = parseFloat(pdDays);
    if (!isFinite(w) || w <= 0 || !isFinite(d) || d <= 0) return null;
    return Math.round(w * d);
  }, [pdWorkers, pdDays]);

  // Show a result once the two defining questions (client + role) are answered.
  const ready = client !== "" && role !== "";

  const outcome = useMemo(() => {
    if (!ready) return null;
    return buildOutcome(client, role, multipleContractors, bigOver30, over500);
  }, [ready, client, role, multipleContractors, bigOver30, over500]);

  // Fire the funnel event once per visit when a result first appears.
  useEffect(() => {
    if (outcome && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("cdm-duty-checker", { notifiable: outcome.notifiable });
    }
  }, [outcome]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_420px]">
      {/* Questions */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">About the project</h2>
        <p className="mt-1 text-sm text-muted">
          Answer these about the construction work you&apos;re planning. CDM 2015 applies to all of it — these
          questions work out which duties apply and whether HSE notification is needed.
        </p>

        <div className="mt-5 space-y-5">
          <Field label="1. Who is the client?" hint="The person or organisation the work is being done for.">
            <SelectInput value={client} onChange={(e) => setClient(e.target.value as ClientType)}>
              <option value="">Choose…</option>
              <option value="domestic">Domestic householder (work on their own home)</option>
              <option value="commercial">Commercial / organisation client</option>
            </SelectInput>
          </Field>

          <Field label="2. Your role on the project">
            <SelectInput value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="">Choose…</option>
              {(Object.keys(ROLE_LABELS) as Exclude<Role, "">[]).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </SelectInput>
          </Field>

          <Field label="3. How many contractors?">
            <CheckboxRow checked={multipleContractors} onChange={setMultipleContractors}>
              More than one contractor will work on the project at any time (including subcontractors).
            </CheckboxRow>
          </Field>

          <Field
            label="4. Scale of the work"
            hint="Tick either box that applies. Either one on its own makes the project notifiable."
          >
            <div className="space-y-2.5">
              <CheckboxRow checked={bigOver30} onChange={setBigOver30}>
                The work is scheduled to last <strong>longer than 30 working days</strong> AND have{" "}
                <strong>more than 20 workers</strong> working at the same time at any point.
              </CheckboxRow>
              <CheckboxRow checked={over500} onChange={setOver500}>
                The work is scheduled to exceed <strong>500 person-days</strong> in total (workers × days).
              </CheckboxRow>
            </div>
          </Field>

          {/* Live person-days helper — the 500 person-day threshold trips people
              up (a 29-day job with 19 workers is still notifiable at 551). */}
          <div className="rounded-lg border border-line bg-mist/60 p-4">
            <div className="text-[0.85rem] font-semibold text-ink">Not sure about person-days? Work it out:</div>
            <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <Field label="Average workers on site">
                <NumberInput min={0} step={1} value={pdWorkers} onChange={(e) => setPdWorkers(e.target.value)} placeholder="e.g. 6" />
              </Field>
              <Field label="Working days (total)">
                <NumberInput min={0} step={1} value={pdDays} onChange={(e) => setPdDays(e.target.value)} placeholder="e.g. 40" />
              </Field>
              <div className="pb-1 text-[0.95rem]" aria-live="polite">
                {personDays !== null ? (
                  <span className={personDays > 500 ? "font-bold text-[#b34e10]" : "font-bold text-ink"}>
                    = {personDays.toLocaleString("en-GB")} person-days
                  </span>
                ) : (
                  <span className="text-muted">= person-days</span>
                )}
              </div>
            </div>
            {personDays !== null && personDays > 500 && !over500 && (
              <p className="mt-2 text-[0.82rem] font-semibold text-[#b34e10]">
                Over 500 — tick the person-days box above. A short job with a big crew can still be notifiable
                (19 workers × 29 days = 551 person-days).
              </p>
            )}
          </div>
        </div>

        {!ready && (
          <p className="mt-5 rounded-lg bg-mist px-4 py-3 text-sm text-muted">
            Choose a client type and your role to see your duties.
          </p>
        )}
      </div>

      {/* Result + printable summary */}
      <div className="space-y-4">
        {outcome ? (
          <>
            <div className="print-area card shadow-soft p-6" id="cdm-record">
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                CDM 2015 duty summary
              </div>
              <h2 className="mt-1 text-lg">
                {client === "domestic" ? "Domestic" : "Commercial"} project —{" "}
                {role ? ROLE_LABELS[role] : ""}
              </h2>
              <p className="mt-0.5 text-[0.8rem] text-muted">
                {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>

              <div className="mt-4">
                <ResultBanner level={outcome.level} title={outcome.verdictTitle}>
                  {outcome.verdictBody}
                </ResultBanner>
              </div>

              <h3 className="mt-5 text-[0.95rem] font-semibold text-ink">Your duties on this project</h3>
              <ul className="mt-3 space-y-2.5">
                {outcome.duties.map((d, i) => (
                  <li key={i} className="flex gap-2.5 text-[0.92rem] leading-relaxed text-body">
                    <svg
                      viewBox="0 0 24 24"
                      className="mt-0.5 h-4 w-4 shrink-0 text-emerald-deep"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <span>{d.text}</span>
                  </li>
                ))}
              </ul>

              {outcome.notifiable && (
                <div className="mt-5 rounded-lg border border-risk-high/40 bg-[#fef0e6] px-4 py-3 text-[0.88rem] leading-relaxed text-[#b34e10]">
                  <strong className="block">F10 notification — what to do</strong>
                  <ul className="mt-1.5 list-disc space-y-1 pl-5">
                    <li>Submit form F10 to HSE online before construction work starts.</li>
                    <li>Display a copy of the F10 notification where it can be read on site.</li>
                    <li>Update the F10 if the particulars (dates, dutyholders, scope) change.</li>
                    <li>Where more than one contractor is involved, appoint the principal designer and principal contractor in writing.</li>
                  </ul>
                </div>
              )}

              {outcome.needsCpp && (
                <div className="mt-5 rounded-lg border border-line bg-mist/60 px-4 py-3 text-[0.88rem] leading-relaxed text-body print-hide">
                  <strong className="text-ink">Construction phase plan needed.</strong> Every project requires
                  one before work starts. A CPP builder is coming soon — in the meantime you can start from a
                  site-specific RAMS:{" "}
                  <a href="/templates/" className="font-semibold text-emerald-deep underline">
                    browse RAMS templates
                  </a>
                  .
                </div>
              )}

              <PrintFooter toolName="CDM duty checker" />
            </div>
            <div className="flex justify-end">
              <PrintButton tool="cdm-duty-checker" label="Print / save summary as PDF" />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Answer the questions to see who must be appointed, whether the project is F10 notifiable, and the
            CDM 2015 duties that apply to your role.
          </div>
        )}
      </div>
    </div>
  );
}
